import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ReviewList from '../components/ReviewList';
import { FYLKER } from '../components/LocationSelector';
import { isDemoMode } from '../demo/useDemoMode';
import { getUserById, getReviewsForUser, ALL_MOCK_JOBS } from '../demo/mockData';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ProfilePage = () => {
  const { id } = useParams();
  const { user: currentUser, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('oversikt');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingJob, setDeletingJob] = useState(null);
  const fileInputRef = useRef(null);

  const isOwnProfile = currentUser?.id === parseInt(id);

  useEffect(() => {
    fetchProfile();
    fetchUserJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProfile = async () => {
    // In demo mode, use mock data
    if (isDemoMode()) {
      const mockUser = getUserById(parseInt(id));
      if (mockUser) {
        const reviews = getReviewsForUser(parseInt(id));
        setProfile({
          ...mockUser,
          reviews: reviews
        });
      }
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/users/${id}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Feil ved henting av profil:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserJobs = async () => {
    // In demo mode, use mock data
    if (isDemoMode()) {
      const userJobs = ALL_MOCK_JOBS.filter(job => job.poster?.id === parseInt(id));
      setJobs(userJobs);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/jobs?poster_id=${id}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Feil ved henting av annonser:', error);
      setJobs([]);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(`${API_URL}/users/${id}/image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProfile(prev => ({ ...prev, profile_image: response.data.profile_image }));
      updateUser({ profile_image: response.data.profile_image });
    } catch (error) {
      console.error('Feil ved opplasting av bilde:', error);
      alert('Kunne ikke laste opp bildet. Prøv igjen.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne annonsen?')) return;

    setDeletingJob(jobId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(prev => prev.filter(job => job.id !== jobId));
    } catch (error) {
      console.error('Feil ved sletting av annonse:', error);
      alert('Kunne ikke slette annonsen. Prøv igjen.');
    } finally {
      setDeletingJob(null);
    }
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/jobs/${jobId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, status: newStatus } : job
      ));
    } catch (error) {
      console.error('Feil ved oppdatering av status:', error);
      alert('Kunne ikke oppdatere status. Prøv igjen.');
    }
  };

  const fylke = profile ? FYLKER.find(f => f.id === profile.location_fylke) : null;
  
  const avgRating = profile?.reviews?.length 
    ? (profile.reviews.reduce((sum, r) => sum + r.rating, 0) / profile.reviews.length).toFixed(1)
    : null;

  const activeJobs = jobs.filter(j => j.status === 'active');
  const completedJobs = jobs.filter(j => j.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="bg-white rounded-xl p-6">
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 bg-slate-200 rounded-full mb-4"></div>
                <div className="h-6 bg-slate-200 rounded w-32 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
            person_off
          </span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Bruker ikke funnet
          </h2>
          <Link to="/" className="text-[#1E3A5F] hover:underline">
            Gå tilbake til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Tilbake-knapp */}
        <Link 
          to="/"
          className="inline-flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] mb-6 transition-colors"
        >
          <span className="material-symbols-outlined">arrow_back</span>
          Tilbake
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Sidebar med profilkort */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              {/* Header med avatar */}
              <div className="flex flex-col items-center text-center mb-6">
                {/* Klikkbart profilbilde */}
                <div className="relative mb-4">
                  {isOwnProfile ? (
                    <label className="cursor-pointer group">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center overflow-hidden relative">
                        {profile.profile_image ? (
                          <img src={`${API_URL.replace('/api', '')}${profile.profile_image}`} alt={profile.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-bold text-3xl">
                            {profile.name?.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          {uploadingImage ? (
                            <span className="material-symbols-outlined text-white animate-spin">progress_activity</span>
                          ) : (
                            <span className="material-symbols-outlined text-white">photo_camera</span>
                          )}
                        </div>
                      </div>
                      <span className="absolute bottom-0 right-0 w-8 h-8 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center shadow-lg">
                        <span className="material-symbols-outlined text-sm">edit</span>
                      </span>
                    </label>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center overflow-hidden">
                      {profile.profile_image ? (
                        <img src={`${API_URL.replace('/api', '')}${profile.profile_image}`} alt={profile.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-bold text-3xl">
                          {profile.name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <h2 className="font-heading font-bold text-xl text-[#0A1929]">{profile.name}</h2>
                  {profile.is_verified && (
                    <span className="material-symbols-outlined text-green-500" title="Verifisert bruker">
                      verified
                    </span>
                  )}
                </div>

                {/* Rating */}
                {avgRating && (
                  <div className="flex items-center gap-1 text-slate-600 mb-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span 
                          key={star}
                          className={`material-symbols-outlined text-lg ${
                            star <= Math.round(avgRating) ? 'text-yellow-500' : 'text-slate-300'
                          }`}
                        >
                          star
                        </span>
                      ))}
                    </div>
                    <span className="font-medium">{avgRating}</span>
                    <span className="text-sm">({profile.reviews?.length} vurderinger)</span>
                  </div>
                )}

                {/* Lokasjon */}
                {fylke && (
                  <div className="flex items-center gap-1 text-slate-500 text-sm">
                    <span className="material-symbols-outlined text-lg">location_on</span>
                    {fylke.name}
                  </div>
                )}
              </div>

              {/* Kontaktinfo */}
              {(isOwnProfile || profile.email) && (
                <div className="space-y-3 border-t border-slate-100 pt-4">
                  {profile.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <span className="material-symbols-outlined text-[#1E3A5F]">email</span>
                      <span className="text-slate-600">{profile.email}</span>
                    </div>
                  )}
                  {profile.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <span className="material-symbols-outlined text-[#1E3A5F]">phone</span>
                      <span className="text-slate-600">{profile.phone}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Statistikk */}
              <div className="flex justify-around mt-6 pt-4 border-t border-slate-100">
                <div className="text-center">
                  <div className="font-heading font-bold text-2xl text-[#0A1929]">
                    {completedJobs.length}
                  </div>
                  <div className="text-xs text-slate-500">Fullførte oppdrag</div>
                </div>
                <div className="text-center">
                  <div className="font-heading font-bold text-2xl text-[#0A1929]">
                    {profile.reviews?.length || 0}
                  </div>
                  <div className="text-xs text-slate-500">Vurderinger</div>
                </div>
              </div>
            </div>
            
            {isOwnProfile && (
              <div className="mt-4 space-y-3">
                <Link 
                  to="/profil/rediger"
                  className="w-full bg-white border border-slate-200 text-[#0A1929] py-3 rounded-xl font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined">edit</span>
                  Rediger profil
                </Link>
                <Link 
                  to="/innstillinger"
                  className="w-full bg-slate-100 text-slate-600 py-3 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined">settings</span>
                  Innstillinger
                </Link>
              </div>
            )}
          </div>

          {/* Hovedinnhold */}
          <div className="md:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="flex border-b border-slate-100">
                <button
                  onClick={() => setActiveTab('oversikt')}
                  className={`flex-1 py-4 text-center font-medium transition-colors ${
                    activeTab === 'oversikt'
                      ? 'text-[#0A1929] border-b-2 border-[#0A1929]'
                      : 'text-slate-500 hover:text-[#0A1929]'
                  }`}
                >
                  Tilbakemeldinger
                </button>
                {(profile.user_type === 'poster' || jobs.length > 0) && (
                  <button
                    onClick={() => setActiveTab('annonser')}
                    className={`flex-1 py-4 text-center font-medium transition-colors ${
                      activeTab === 'annonser'
                        ? 'text-[#0A1929] border-b-2 border-[#0A1929]'
                        : 'text-slate-500 hover:text-[#0A1929]'
                    }`}
                  >
                    {isOwnProfile ? 'Mine oppdrag' : 'Annonser'}
                    {activeJobs.length > 0 && (
                      <span className="ml-2 bg-[#1E3A5F] text-white text-xs px-2 py-0.5 rounded-full">
                        {activeJobs.length}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Tab content */}
            {activeTab === 'oversikt' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <ReviewList reviews={profile.reviews} />
              </div>
            )}

            {activeTab === 'annonser' && (
              <div>
                {isOwnProfile && activeJobs.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-heading font-semibold text-lg text-[#0A1929] mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-green-500">check_circle</span>
                      Aktive oppdrag
                    </h3>
                    <div className="space-y-4">
                      {activeJobs.map((job) => (
                        <div 
                          key={job.id}
                          className="bg-white rounded-xl shadow-sm border border-slate-100 p-4"
                        >
                          <div className="flex gap-4">
                            {/* Thumbnail */}
                            <Link to={`/annonse/${job.id}`} className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {job.images?.[0] ? (
                                <img src={job.images[0].startsWith('http') ? job.images[0] : `${API_URL.replace('/api', '')}${job.images[0]}`} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="material-symbols-outlined text-white/30 text-2xl">work</span>
                              )}
                            </Link>

                            {/* Info */}
                            <div className="flex-grow min-w-0">
                              <Link to={`/annonse/${job.id}`} className="block">
                                <h4 className="font-medium text-[#0A1929] truncate hover:text-[#1E3A5F]">{job.title}</h4>
                              </Link>
                              <p className="text-sm text-slate-500 line-clamp-1 mb-2">{job.description}</p>
                              <span className="font-heading font-bold text-[#1E3A5F]">
                                {job.price?.toLocaleString('nb-NO')} kr
                              </span>
                            </div>

                            {/* Actions */}
                            {isOwnProfile && (
                              <div className="flex flex-col gap-2">
                                <Link
                                  to={`/rediger-annonse/${job.id}`}
                                  className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-center"
                                >
                                  Rediger
                                </Link>
                                <button
                                  onClick={() => handleUpdateJobStatus(job.id, 'completed')}
                                  className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                >
                                  Fullfør
                                </button>
                                <button
                                  onClick={() => handleDeleteJob(job.id)}
                                  disabled={deletingJob === job.id}
                                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                                >
                                  {deletingJob === job.id ? '...' : 'Slett'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {jobs.length > 0 ? (
                  <div className="space-y-4">
                    {!isOwnProfile && (
                      <h3 className="font-heading font-semibold text-lg text-[#0A1929] mb-4">
                        Alle annonser
                      </h3>
                    )}
                    {(isOwnProfile ? jobs.filter(j => j.status !== 'active') : jobs).map((job) => (
                      <Link 
                        key={job.id}
                        to={`/annonse/${job.id}`}
                        className="block bg-white rounded-xl shadow-sm border border-slate-100 p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex gap-4">
                          {/* Thumbnail */}
                          <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {job.images?.[0] ? (
                              <img src={job.images[0].startsWith('http') ? job.images[0] : `${API_URL.replace('/api', '')}${job.images[0]}`} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="material-symbols-outlined text-white/30 text-2xl">work</span>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-[#0A1929] truncate">{job.title}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                job.status === 'active' 
                                  ? 'bg-green-100 text-green-700' 
                                  : job.status === 'completed'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-slate-100 text-slate-600'
                              }`}>
                                {job.status === 'active' ? 'Aktiv' : job.status === 'completed' ? 'Fullført' : 'Avbrutt'}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 line-clamp-1 mb-2">{job.description}</p>
                            <span className="font-heading font-bold text-[#1E3A5F]">
                              {job.price?.toLocaleString('nb-NO')} kr
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                    <span className="material-symbols-outlined text-4xl text-slate-300 mb-2 block">
                      work_off
                    </span>
                    <p className="text-slate-500">Ingen annonser lagt ut ennå</p>
                    {isOwnProfile && (
                      <Link 
                        to="/opprett-annonse"
                        className="inline-block mt-4 bg-[#0A1929] text-white px-6 py-2 rounded-lg hover:bg-[#1E3A5F] transition-colors"
                      >
                        Opprett din første annonse
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
